import "./shipment-item.css";

import type { TextBoxChangeEvent } from "@tql/ui-components-react/kendo/inputs";
import { Error, Label } from "@tql/ui-components-react/kendo/labels";
import {
	NumericTextBox,
	TextBox,
} from "@tql/ui-components-react/tql/inputs/beta";
import { useContext } from "react";

import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import { useQuoteDetailsPageLoadQueries } from "~/features/quote/hooks/use-quote-details-hooks.ts";
import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";
import type { HazmatClass, HazmatGroup } from "~/shared/schemas/hazmat-schema";
import type { UnitType } from "~/shared/schemas/unit-type-schema";
import type { ShipmentItemProps } from "~/shared/types/shipment-summary-types";

import { HazmatDetailRow } from "../hazmat-detail-row/hazmat-detail-row.tsx";

type ShipmentItemViewProps = ShipmentItemProps & {
	unitTypes: UnitType[] | undefined;
	hazmatClasses: HazmatClass[] | undefined;
	hazmatGroups: HazmatGroup[] | undefined;
	isSortAndSegregate: boolean;
	validationErrors?: Set<string>;
	onClearFieldError?: (itemId: number, fieldKey: string) => void;
};

const HazmatEnabledView = ({
	item,
	handleItemUpdate,
	hazmatClasses,
	hazmatGroups,
	isSortAndSegregate,
	validationErrors,
	onClearFieldError,
}: ShipmentItemViewProps) => {
	return (
		<div className={"shipment-item-wrapper"}>
			<div className={"shipment-item-row"}>
				<div className={"k-form-field"}>
					<div className={"k-label k-form-label"}>Qty</div>
					<div>{item.quantity}</div>
				</div>
				<div className={"k-form-field shipment-details-unit"}>
					<Label editorId={`name-${item.id}`}>Unit</Label>
					<div>
						<div>{item.unit.name}</div>
					</div>
				</div>
				<div className={"k-form-field"}>
					<div className={"k-label k-form-label"}>Total Weight</div>
					<div>{item.weight ?? 0} lb</div>
				</div>
				<div className={"k-form-field"}>
					<div className={"k-label k-form-label"}>Dimensions</div>
					<div>
						{item.dimensionLength} in x {item.dimensionWidth} in x{" "}
						{item.dimensionHeight} in
					</div>
				</div>
				<div className={"shipment-details-description"}>
					<Label editorId={`description-${item.id}`}>
						Commodity Description <span className={"required"}>&nbsp;*</span>
					</Label>
					<TextBox
						data-testid={"description-input"}
						id={`description-${item.id}`}
						name={`description-${item.id}`}
						onChange={(event: TextBoxChangeEvent) => {
							onClearFieldError?.(item.id, "description");
							handleItemUpdate(item, "description", event.value as string);
						}}
						valid={!validationErrors?.has("description")}
						value={item.description || ""}
					/>
					{validationErrors?.has("description") ? (
						<Error>Required</Error>
					) : null}
				</div>
				<div className={"k-form-field"}>
					<div className={"k-label k-form-label"}>Class</div>
					<div>{item.class.name}</div>
				</div>
				<div className={"shipment-details-nmfc"}>
					<Label editorId={`nmfc-${item.id}`}>
						NMFC{" "}
						{item.isHazmat ? <span className={"required"}>&nbsp;*</span> : null}
					</Label>
					<TextBox
						data-testid={"nmfc-input"}
						id={`nmfc-${item.id}`}
						name={`nmfc-${item.id}`}
						onChange={(event: TextBoxChangeEvent) => {
							const sanitizedValue = (event.value as string).replaceAll(
								/[^0-9-]/g,
								"",
							);
							onClearFieldError?.(item.id, "nmfc");
							handleItemUpdate(item, "nmfc", sanitizedValue);
						}}
						valid={!(item.isHazmat && validationErrors?.has("nmfc"))}
						value={item.nmfc || ""}
					/>
					{item.isHazmat && validationErrors?.has("nmfc") ? (
						<Error>Required</Error>
					) : null}
				</div>
				<div className={"k-form-field"}>
					<div className={"k-label k-form-label"}>Stackable</div>
					<div>{item.isStackable ? "Yes" : "No"}</div>
				</div>
				<div className={"k-form-field"}>
					<div className={"k-label k-form-label"}>Hazmat</div>
					<div>{item.isHazmat ? "Yes" : "No"}</div>
				</div>
				<div>
					<Label editorId={`piece-count-${item.id}`}>
						Piece Count{" "}
						{isSortAndSegregate || item.isHazmat ? (
							<span className={"required"}>&nbsp;*</span>
						) : null}
					</Label>
					<NumericTextBox
						data-testid={"piece-count-input"}
						id={`piece-count-${item.id}`}
						name={`piece-count-${item.id}`}
						onChange={({ value }: { value: number | null }) => {
							onClearFieldError?.(item.id, "pieceCount");
							const pieceCount = value != null && value > 0 ? value : null;
							handleItemUpdate(item, "pieceCount", pieceCount);
						}}
						readOnly={isSortAndSegregate}
						valid={!validationErrors?.has("pieceCount")}
						value={
							item.pieceCount != null && item.pieceCount > 0
								? item.pieceCount
								: null
						}
					/>
					{validationErrors?.has("pieceCount") ? <Error>Required</Error> : null}
				</div>
			</div>

			{item.isHazmat ? (
				<HazmatDetailRow
					handlingUnit={item}
					hazmatClasses={hazmatClasses}
					hazmatGroups={hazmatGroups}
					onClearFieldError={onClearFieldError}
					validationErrors={validationErrors}
				/>
			) : null}
		</div>
	);
};

const HazmatDisabledView = ({
	item,
	handleItemUpdate,
	validationErrors,
	onClearFieldError,
}: Omit<
	ShipmentItemViewProps,
	"isSortAndSegregate" | "hazmatClasses" | "hazmatGroups"
>) => {
	return (
		<div className={"shipment-item-row"}>
			<div className={"shipment-details-description"}>
				<Label editorId={`description-${item.id}`}>
					Commodity Description <span className={"required"}>&nbsp;*</span>
				</Label>
				<TextBox
					data-testid={"description-input"}
					id={`description-${item.id}`}
					name={`description-${item.id}`}
					onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
						onClearFieldError?.(item.id, "description");
						handleItemUpdate(item, "description", e.target.value);
					}}
					valid={!validationErrors?.has("description")}
					value={item.description || ""}
				/>
				{validationErrors?.has("description") ? <Error>Required</Error> : null}
			</div>
			<div className={"shipment-details-nmfc"}>
				<Label editorId={`nmfc-${item.id}`}>
					NMFC{" "}
					{item.isHazmat ? <span className={"required"}>&nbsp;*</span> : null}
				</Label>

				<TextBox
					data-testid={"nmfc-input"}
					id={`nmfc-${item.id}`}
					name={`nmfc-${item.id}`}
					onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
						const sanitizedValue = e.target.value.replaceAll(/[^0-9-]/g, "");
						onClearFieldError?.(item.id, "nmfc");
						handleItemUpdate(item, "nmfc", sanitizedValue);
					}}
					valid={!(item.isHazmat && validationErrors?.has("nmfc"))}
					value={item.nmfc || ""}
				/>
				{item.isHazmat && validationErrors?.has("nmfc") ? (
					<Error>Required</Error>
				) : null}
			</div>
			<div className={"k-form-field"}>
				<div className={"k-label k-form-label"}>Class</div>
				<div>{item.class.name}</div>
			</div>
			<div className={"k-form-field"}>
				<div className={"k-label k-form-label"}>Qty</div>
				<div>{item.quantity}</div>
			</div>
			<div className={"k-form-field shipment-details-unit"}>
				<Label editorId={`name-${item.id}`}>Unit</Label>
				<div>
					<div>{item.unit.name}</div>
				</div>
			</div>
			<div className={"k-form-field"}>
				<div className={"k-label k-form-label"}>Total Weight</div>
				<div>{item.weight ?? 0} lb</div>
			</div>
			<div className={"k-form-field"}>
				<div className={"k-label k-form-label"}>Dimensions</div>
				<div>
					{item.dimensionLength} in x {item.dimensionWidth} in x{" "}
					{item.dimensionHeight} in
				</div>
			</div>
			<div className={"k-form-field"}>
				<div className={"k-label k-form-label"}>Stackable</div>
				<div>{item.isStackable ? "Yes" : "No"}</div>
			</div>
			<div className={"k-form-field"}>
				<div className={"k-label k-form-label"}>Hazmat</div>
				<div>{item.isHazmat ? "Yes" : "No"}</div>
			</div>
		</div>
	);
};

export const ShipmentItem = ({
	showHazmatDetails = false,
	item,
	handleItemUpdate,
	validationErrors,
	onClearFieldError,
}: ShipmentItemProps & {
	showHazmatDetails?: boolean;
	validationErrors?: Set<string>;
	onClearFieldError?: (itemId: number, fieldKey: string) => void;
}) => {
	const { isReady: hazmatFfIsReady, isEnabled: hazmatFfIsEnabled } =
		useFeatureFlagGate("traxLtlHazmatItemization");
	const { data } = useQuoteDetailsPageLoadQueries();
	const { quoteState } = useContext(QuoteDetailsContext);
	const unitTypes = data?.unitTypes;
	const hazmatClasses = data?.hazmatClasses;
	const hazmatGroups = data?.hazmatGroups;

	const isSortAndSegregate = quoteState.deliveryServices.some(
		(service) => service.name === "Sort and Segregate",
	);

	return showHazmatDetails || (hazmatFfIsEnabled && hazmatFfIsReady) ? (
		<HazmatEnabledView
			handleItemUpdate={handleItemUpdate}
			hazmatClasses={hazmatClasses}
			hazmatGroups={hazmatGroups}
			isSortAndSegregate={isSortAndSegregate}
			item={item}
			onClearFieldError={onClearFieldError}
			unitTypes={unitTypes}
			validationErrors={validationErrors}
		/>
	) : (
		<HazmatDisabledView
			handleItemUpdate={handleItemUpdate}
			item={item}
			onClearFieldError={onClearFieldError}
			unitTypes={unitTypes}
			validationErrors={validationErrors}
		/>
	);
};
